import { useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { Input } from "@/components/ui/Input/Input";
import { Textarea } from "@/components/ui/Textarea/Textarea";
import { AppShell } from "@/components/app/AppShell";
import { useAuth } from "@/lib/AuthContext";
import {
  useBrand,
  useInputs,
  useCreateInput,
  useDeleteInput,
  inputId,
  type InputRecord,
} from "@/lib/queries";
import styles from "./Inputs.module.css";

/**
 * Inputs — the brand's raw "Inputs" feed: notes and links the user drops in
 * to seed draft generation, read alongside the brand brain. Wired to
 * `.../brands/:brand/inputs` via the TanStack Query hooks in
 * `apps/web/src/lib/queries.ts`. (Photos live in the Media Library.)
 */

function errMessage(e: unknown): string {
  if (e && typeof e === "object" && "message" in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === "string" && m.trim()) return m;
  }
  return "Something went wrong. Please try again.";
}

function formatTime(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function Inputs() {
  const { ws, brand } = useParams<{ ws: string; brand: string }>();
  const auth = useAuth();
  const brandQuery = useBrand(ws, brand);
  const inputsQuery = useInputs(ws, brand);
  const createInput = useCreateInput(ws, brand);
  const deleteInput = useDeleteInput(ws, brand);

  const [composerOpen, setComposerOpen] = useState(false);
  const [text, setText] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");

  if (auth.status === "anonymous") {
    return <Navigate to={`/signin?next=/w/${ws}/b/${brand}/inputs`} replace />;
  }

  const b = brandQuery.data?.brand;
  const inputs = inputsQuery.data?.inputs ?? [];

  function resetComposer() {
    setText("");
    setSourceUrl("");
    setComposerOpen(false);
    createInput.reset();
  }

  function handleAdd() {
    const trimmed = text.trim();
    if (!trimmed) return;
    createInput.mutate(
      { text: trimmed, ...(sourceUrl.trim() ? { sourceUrl: sourceUrl.trim() } : {}) },
      { onSuccess: () => resetComposer() },
    );
  }

  return (
    <AppShell currentWorkspaceId={ws}>
      <div className={styles.page}>
        <Btn
          href={`/w/${ws}/b/${brand}`}
          variant="ghost"
          size="sm"
          iconLeft={<Icon name="arrow-left" size={13} />}
        >
          Back to brand
        </Btn>

        <header className={styles.head}>
          <h1 className={styles.title}>
            <Icon name="edit" size={20} /> Inputs{b ? ` · ${b.name}` : ""}
          </h1>
          <div className={styles.headActions}>
            <Btn
              variant="primary"
              onClick={() => setComposerOpen((v) => !v)}
              iconLeft={<Icon name="send" size={14} />}
            >
              Add input
            </Btn>
          </div>
        </header>

        <section className={styles.intro}>
          <p className={styles.introLede}>
            Inputs are the raw material you drop in to seed draft generation —
            quick notes and links to articles or posts. The generator reads
            your inputs alongside the brand brain so drafts reflect what
            you’ve been collecting. (Reference photos live in the Media
            Library.)
          </p>
        </section>

        {composerOpen && (
          <section className={styles.composer}>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Drop a note, idea, or paste text to seed a draft…"
              rows={3}
              aria-label="Input text"
            />
            <Input
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              placeholder="Optional link (https://…)"
              icon="link"
              aria-label="Optional source link"
            />
            <div className={styles.composerActions}>
              {createInput.isError && (
                <span className={styles.fieldError}>{errMessage(createInput.error)}</span>
              )}
              <Btn variant="ghost" size="sm" onClick={resetComposer} disabled={createInput.isPending}>
                Cancel
              </Btn>
              <Btn
                variant="primary"
                size="sm"
                onClick={handleAdd}
                disabled={!text.trim() || createInput.isPending}
                loading={createInput.isPending}
              >
                {createInput.isPending ? "Adding…" : "Add input"}
              </Btn>
            </div>
          </section>
        )}

        <section className={styles.listWrap}>
          <div className={styles.listHead}>
            <h2 className={styles.listTitle}>Your inputs</h2>
            <span className={styles.count}>{inputsQuery.isSuccess ? inputs.length : "—"}</span>
          </div>

          {inputsQuery.isLoading ? (
            <div className={styles.emptyPane} role="note">
              <p className={styles.emptyHint}>Loading inputs…</p>
            </div>
          ) : inputsQuery.isError ? (
            <div className={styles.emptyPane} role="alert">
              <Icon name="alert" size={22} />
              <p>Couldn’t load inputs.</p>
              <p className={styles.emptyHint}>{errMessage(inputsQuery.error)}</p>
            </div>
          ) : inputs.length === 0 ? (
            <div className={styles.emptyPane} role="note">
              <Icon name="edit" size={22} />
              <p>No inputs yet.</p>
              <p className={styles.emptyHint}>
                Notes and links you add appear here, newest first, ready to
                seed your next batch of drafts.
              </p>
            </div>
          ) : (
            <ul className={styles.inputList}>
              {inputs.map((it: InputRecord) => {
                const id = inputId(it);
                return (
                  <li key={id} className={styles.inputItem}>
                    <div className={styles.inputBody}>
                      <p className={styles.inputText}>{it.input.text}</p>
                      {it.input.sourceUrl && (
                        <a
                          className={styles.inputLink}
                          href={it.input.sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Icon name="link" size={12} /> {it.input.sourceUrl}
                        </a>
                      )}
                      {it.input.notes && <p className={styles.inputNotes}>{it.input.notes}</p>}
                      <span className={styles.inputMeta}>{formatTime(it.createdAt)}</span>
                    </div>
                    <Btn
                      variant="ghost"
                      size="sm"
                      iconLeft={<Icon name="x" size={13} />}
                      disabled={deleteInput.isPending}
                      onClick={() => deleteInput.mutate({ id })}
                      aria-label="Delete input"
                    >
                      Delete
                    </Btn>
                  </li>
                );
              })}
            </ul>
          )}
          {deleteInput.isError && (
            <p className={styles.fieldError}>{errMessage(deleteInput.error)}</p>
          )}
        </section>
      </div>
    </AppShell>
  );
}
